// apps/frontend/src/components/common/Skeleton.tsx
// Skeletons Glowp — primitives + variants composés. Palette beige/cream avec
// shimmer doré qui glisse. À utiliser partout en place des spinners pour que
// les pages "respirent" pendant le chargement au lieu de bloquer sur une roue.
//
// Usage :
//   <Skeleton className="h-4 w-24" />              // texte
//   <Skeleton circle className="w-10 h-10" />       // avatar
//   <ProductCardSkeleton />
//   <UserRowSkeleton />
//   <MetricCardSkeleton />

import React from 'react';

interface SkeletonProps {
  className?: string;
  /** Forme ronde (avatars, badges, icônes) — sinon rectangle */
  circle?: boolean;
  /** Coupe les coins arrondis du wrapper (rare) */
  noRounded?: boolean;
  /** Désactive l'animation (utile dans une preview/storybook) */
  static?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  circle = false,
  noRounded = false,
  static: isStatic = false,
}) => {
  const shape = circle ? 'rounded-full' : noRounded ? '' : 'rounded-lg';
  return (
    <div
      className={`glowp-skeleton ${shape} ${className} ${
        isStatic ? '' : 'glowp-skeleton--anim'
      }`}
      aria-hidden="true"
    />
  );
};

/**
 * Card produit (catalogue, recherche, favoris, etc.) — matche la structure
 * d'une vraie product card pour éviter le shift visuel au load.
 */
export const ProductCardSkeleton: React.FC = () => (
  <div className="flex flex-col gap-2">
    <Skeleton className="aspect-[4/5] w-full rounded-2xl" />
    <div className="flex items-center gap-2 px-1 pt-1">
      <Skeleton circle className="w-5 h-5 flex-shrink-0" />
      <Skeleton className="h-3 w-20" />
    </div>
    <Skeleton className="h-3.5 w-3/4 mx-1" />
    <Skeleton className="h-3 w-1/2 mx-1" />
    <div className="flex items-center justify-between mx-1 pt-0.5">
      <Skeleton className="h-4 w-14" />
      <Skeleton circle className="w-7 h-7" />
    </div>
  </div>
);

/**
 * Grille de N product cards (responsive, suit la grille Glowp 2/3/4 cols).
 */
export const ProductGridSkeleton: React.FC<{ count?: number }> = ({
  count = 8,
}) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Ligne user (admin users list, followers list, etc.) — avatar + nom + email
 * + zone action à droite. Garde la même hauteur que la vraie row pour
 * minimiser le shift.
 */
export const UserRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 py-3 px-4">
    <Skeleton circle className="w-10 h-10 flex-shrink-0" />
    <div className="flex-1 min-w-0 space-y-1.5">
      <Skeleton className="h-3.5 w-32" />
      <Skeleton className="h-3 w-44" />
    </div>
    <Skeleton className="h-7 w-16 rounded-full flex-shrink-0" />
  </div>
);

/**
 * Liste de N user rows.
 */
export const UserListSkeleton: React.FC<{ count?: number }> = ({
  count = 6,
}) => (
  <div className="divide-y divide-gray-100">
    {Array.from({ length: count }).map((_, i) => (
      <UserRowSkeleton key={i} />
    ))}
  </div>
);

/**
 * Card de métrique admin (KPI block — icon + label + valeur + sous-texte).
 */
export const MetricCardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-gray-200 p-3 space-y-1.5">
    <div className="flex items-center gap-1.5">
      <Skeleton circle className="w-3.5 h-3.5" />
      <Skeleton className="h-3 w-16" />
    </div>
    <Skeleton className="h-7 w-12" />
    <Skeleton className="h-2.5 w-20" />
  </div>
);

/**
 * Grille de N metric cards (style admin KPI row).
 */
export const MetricGridSkeleton: React.FC<{ count?: number }> = ({
  count = 6,
}) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
    {Array.from({ length: count }).map((_, i) => (
      <MetricCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Conversation row (messagerie) — avatar + nom + preview + heure.
 */
export const ConversationRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 py-3 px-4">
    <Skeleton circle className="w-12 h-12 flex-shrink-0" />
    <div className="flex-1 min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-2.5 w-10 flex-shrink-0" />
      </div>
      <Skeleton className="h-3 w-3/4" />
    </div>
  </div>
);

/**
 * Liste de N conversation rows.
 */
export const ConversationListSkeleton: React.FC<{ count?: number }> = ({
  count = 5,
}) => (
  <div className="divide-y divide-gray-100">
    {Array.from({ length: count }).map((_, i) => (
      <ConversationRowSkeleton key={i} />
    ))}
  </div>
);

/**
 * Skeleton d'une page produit complète (header + photos + titre + prix +
 * description + cta). Plus complexe car la page est dense.
 */
export const ProductDetailSkeleton: React.FC = () => (
  <div className="max-w-4xl mx-auto p-4 space-y-5">
    {/* Photos */}
    <Skeleton className="aspect-square w-full rounded-2xl" />
    {/* Header vendeur */}
    <div className="flex items-center gap-3">
      <Skeleton circle className="w-11 h-11" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-8 w-20 rounded-full" />
    </div>
    {/* Titre */}
    <div className="space-y-2">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-5 w-24" />
    </div>
    {/* Description */}
    <div className="space-y-2 pt-2">
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-3.5 w-5/6" />
      <Skeleton className="h-3.5 w-2/3" />
    </div>
    {/* CTAs */}
    <div className="flex gap-2 pt-2">
      <Skeleton className="h-11 flex-1 rounded-xl" />
      <Skeleton className="h-11 w-11 rounded-xl" />
    </div>
  </div>
);

/**
 * Skeleton d'une header de boutique vendeur (banner + avatar + nom + stats).
 */
export const ShopHeaderSkeleton: React.FC = () => (
  <div className="space-y-3">
    <Skeleton className="h-32 sm:h-40 w-full rounded-2xl" />
    <div className="flex items-center gap-3 px-2">
      <Skeleton
        circle
        className="w-16 h-16 -mt-8 ring-4 ring-white flex-shrink-0"
      />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
      <Skeleton className="h-9 w-20 rounded-full" />
    </div>
    <div className="flex gap-4 px-2 pt-1">
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Styles globaux du skeleton (background base Glowp + shimmer doré qui glisse).
// Injectés via <style> dans le composant racine pour éviter une dépendance
// externe à un fichier CSS. Conserve la palette brand : aucune teinte
// étrangère (pas de gris neutre, on reste dans le cream/sand Glowp).
// ---------------------------------------------------------------------------

export const SkeletonStyles: React.FC = () => (
  <style>{`
    .glowp-skeleton {
      background-color: #F0E7D6;
      position: relative;
      overflow: hidden;
    }
    .glowp-skeleton--anim::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.55) 50%,
        transparent 100%
      );
      transform: translateX(-100%);
      animation: glowpSkeletonShimmer 1.5s ease-in-out infinite;
    }
    @keyframes glowpSkeletonShimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @media (prefers-reduced-motion: reduce) {
      .glowp-skeleton--anim::after {
        animation: none;
      }
    }
  `}</style>
);

export default Skeleton;
